// ---------------------------------------------------------------------------
// The `laFollette` theme — a school-specific theme for Robert M. La Follette
// High School (Madison, WI; Home of the Lancers). Authored DRY as *deviations*
// from the `default` base, like every other theme (see professional.ts).
//
// BRAND SOURCE OF TRUTH: colors are sampled directly from the official Lancer "L"
// logo — maroon fill #A12135, silver outline #9C9FA1, white inner outline. The
// school's official colors are maroon and gray; the design pairs them with a
// charcoal secondary for a collegiate-varsity feel.
//
// SIGNATURE MOTIF — "maroon bar + silver outline". The logo is a maroon shape
// edged in silver, so every structural maroon bar here carries a thin silver
// pinstripe on one edge (the title-slide base band, the content-slide spine, the
// two-column divider). This — plus the actual L emblem on the title slide and the
// 45°-rotated diamond "kicker" marks — is intentionally distinct from the other
// themes (none use an image, a rotated element, or a maroon/silver pairing).
//
// IMAGE DECORATION: the title slide places the live logo via an `image`
// decoration pointing at the school's public CDN URL. This is a deliberate
// external dependency (the only theme with one): the URL must stay reachable for
// the emblem to render in the editor and to be fetched server-side by the Google
// Slides exporter's `createImage`. A data-URI would render in-app but Google
// Slides rejects non-URL sources, so the hosted URL is the only form that works
// end-to-end.
// ---------------------------------------------------------------------------

import type { ThemeDefinition } from '../registry';

/** Official Lancer "L" logo (school CDN). Public + stable (versioned path); the
 *  Google Slides exporter fetches this URL at export time, so it must stay
 *  publicly reachable. */
const LANCER_LOGO_URL =
  'https://resources.finalsite.net/images/f_auto,q_auto,t_image_size_2/v1759427364/madisonk12wius/yreivyjoponplxstabje/la-follette-logo.png';

export const LA_FOLLETTE_THEME: ThemeDefinition = {
  themeId: 'laFollette',
  name: 'La Follette',
  baseThemeId: 'default',
  palette: {
    background: '#F4F5F6', // cool silver-tinted paper (echoes the logo's gray)
    surface: '#E6E8EA', // light steel panel
    text: '#201D1E', // near-black with a faint warm cast
    textMuted: '#6B6E72', // cool gray
    primary: '#A12135', // La Follette maroon (logo fill)
    primaryContrast: '#FFFFFF',
    secondary: '#37373A', // graphite/charcoal — the varsity third color
    secondaryContrast: '#FFFFFF',
    accent: '#9C9FA1', // La Follette silver (logo outline)
  },
  fonts: {
    headingFont: 'Tahoma, Geneva, sans-serif', // sturdy + unused by sibling themes
    bodyFont: 'Georgia, serif', // academic readability
  },
  layouts: {
    // Title slide: light field, the live Lancer "L" emblem on the right, title +
    // subtitle on the left, a 45° maroon kicker diamond, a crisp maroon title
    // rule, and a full-bleed maroon base band edged with a silver pinstripe.
    TITLE: {
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 96, y: 152, width: 500, height: 124 },
          textStyle: { fontSize: 50, color: '$primary', lineHeight: 1.02 },
        },
        {
          placeholderId: 'SUBTITLE',
          position: { x: 96, y: 332, width: 500, height: 60 },
          textStyle: { fontSize: 24, color: '$secondary', lineHeight: 1.25 },
        },
      ],
      decoration: [
        // Maroon base band ("the field") + silver outline pinstripe on its top edge.
        {
          type: 'shape',
          position: { x: 0, y: 476, width: 960, height: 64, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
        {
          type: 'shape',
          position: { x: 0, y: 470, width: 960, height: 5, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent', strokeWidth: 0 },
        },
        // The live Lancer "L" emblem on the right (square box ⇒ no distortion).
        {
          type: 'image',
          position: { x: 640, y: 104, width: 280, height: 280, rotation: 0, zIndex: 0 },
          content: {
            src: LANCER_LOGO_URL,
            alt: 'La Follette High School Lancers logo',
            objectFit: 'contain',
          },
        },
        // Crisp maroon title rule.
        {
          type: 'shape',
          position: { x: 96, y: 300, width: 148, height: 8, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
      ],
    },
    // Content slide: light bg inherited; a maroon left spine edged in silver
    // (the logo motif), a silver kicker diamond riding the spine, and a maroon
    // title rule. Title goes maroon; body color inherits ($text).
    TITLE_AND_BODY: {
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 84, y: 46, width: 792 },
          textStyle: { backgroundColor: '$primary', color: '$primaryContrast' },
        },
        {
          placeholderId: 'BODY',
          position: { x: 84, y: 150, width: 792, height: 338 },
        },
      ],
      decoration: [
        // Maroon left spine + silver outline pinstripe on its right edge.
        {
          type: 'shape',
          position: { x: 0, y: 0, width: 18, height: 540, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
        {
          type: 'shape',
          position: { x: 18, y: 0, width: 5, height: 540, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent', strokeWidth: 0 },
        },
        // Silver kicker diamond riding the maroon spine (silver-on-maroon ⇒ logo echo).
        {
          type: 'shape',
          position: { x: 4, y: 24, width: 10, height: 10, rotation: 45, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent', strokeWidth: 0 },
        },
      ],
    },
    // Two columns: same spine + kicker + title rule, plus a maroon center divider
    // ("the lance") edged in silver down the gutter. Columns nudged to clear both.
    TITLE_AND_TWO_COLUMNS: {
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 84, y: 46, width: 792 },
          textStyle: { color: '$primary' },
        },
        {
          placeholderId: 'LEFT_BODY',
          position: { x: 84, y: 150, width: 372, height: 338 },
        },
        {
          placeholderId: 'RIGHT_BODY',
          position: { x: 512, y: 150, width: 372, height: 338 },
        },
      ],
      decoration: [
        // Maroon left spine + silver pinstripe.
        {
          type: 'shape',
          position: { x: 0, y: 0, width: 18, height: 540, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
        {
          type: 'shape',
          position: { x: 18, y: 0, width: 5, height: 540, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent', strokeWidth: 0 },
        },
        // Silver kicker diamond on the spine.
        {
          type: 'shape',
          position: { x: 4, y: 24, width: 10, height: 10, rotation: 45, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$accent', strokeWidth: 0 },
        },
        // Maroon title rule.
        {
          type: 'shape',
          position: { x: 84, y: 124, width: 148, height: 6, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
        // Center divider ("the lance"): maroon bar, in the gutter
        // between the columns (left ends x=456, right starts x=512 ⇒ center ≈484).
        {
          type: 'shape',
          position: { x: 480, y: 150, width: 5, height: 332, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
      ],
    },
    // BLANK inherits the base's empty decoration + (token-resolved) light background.
  },
};
